import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Modal from '../../components/Modal';
import ProductAddonService from '../../components/ProductAddonService';
import PlateformService from '../../components/PlateformService';
import ProductService from '../../components/ProductService';

const createEmptyAddon = () => ({
  name: '',
  input_type: 'select',
  required: false,
  multiple: false,
  is_inventory: false,
  plateforms_keys: [],
  options: [],
});

const addonInputTypes = [
  { value: 'select', label: 'Liste déroulante' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Cases à cocher' },
  { value: 'text', label: 'Texte' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'color', label: 'Couleur' },
  { value: 'size', label: 'Taille' },
  { value: 'product', label: 'Produit lié' },
];

const extractId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value._id) return value._id;
    if (value.id) return value.id;
    if (value.value) return value.value;
    if (typeof value.toString === 'function') return value.toString();
  }
  return '';
};

const isWooCommercePlateform = (plateform) => {
  const value = `${plateform?.type || ''} ${plateform?.name || ''} ${plateform?.label || ''}`.toLowerCase();
  return value.includes('woocommerce') || value === 'woo';
};

const sanitizePlateformKeys = (keys = [], plateformById = {}) =>
  keys
    .map((entry) => {
      const plateformId = extractId(entry?.plateform_id);
      const remoteId = String(entry?.remote_id || '').trim();
      const isWooCommerce = isWooCommercePlateform(plateformById[plateformId]);

      if (!plateformId || (!remoteId && !isWooCommerce)) {
        return null;
      }

      return {
        plateform_id: plateformId,
        remote_id: remoteId,
      };
    })
    .filter(Boolean);

const sanitizeOptionProducts = (products = []) =>
  (products || [])
    .filter((product) => extractId(product.product_id || product.product || product.id))
    .map((product) => {
      const productId = extractId(product.product_id || product.product || product.id);
      const quantityNumber = Number(product.quantity);
      const sanitizedQuantity =
        Number.isFinite(quantityNumber) && quantityNumber >= 0 ? quantityNumber : 1;

      return {
        product_id: productId,
        quantity: sanitizedQuantity,
      };
    });

const normalizeNumericInput = (value, defaultValue = 0) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : defaultValue;
};

const sanitizeOptions = (options = [], plateformById = {}) =>
  options
    .filter((option) => option && option.name && option.name.trim() !== '')
    .map((option) => {
      const sanitized = {
        name: option.name.trim(),
        price: normalizeNumericInput(option.price),
        price_delta: normalizeNumericInput(option.price_delta ?? option.priceDelta ?? option.price),
        sale_price_delta: normalizeNumericInput(
          option.sale_price_delta ?? option.salePriceDelta
        ),
        image_url: (option.image_url || option.imageUrl || '').trim(),
        color: (option.color || '').trim(),
      };

      if (option._id) {
        sanitized._id = option._id;
      }

      const plateformKeys = sanitizePlateformKeys(option.plateforms_keys, plateformById);
      if (plateformKeys.length) {
        sanitized.plateforms_keys = plateformKeys;
      }

      sanitized.products = sanitizeOptionProducts(option.products);

      return sanitized;
    });

const sanitizeAddonPayload = (data, plateformById = {}) => {
  const payload = {
    name: (data.name || '').trim(),
    input_type: data.input_type || data.type || 'select',
    required: !!data.required,
    multiple: !!data.multiple,
    is_inventory: !!data.is_inventory,
  };

  const addonKeys = sanitizePlateformKeys(data.plateforms_keys, plateformById);
  payload.plateforms_keys = addonKeys;

  const options = sanitizeOptions(data.options, plateformById);
  payload.options = options;

  return payload;
};

const optionColors = ['#1976d2', '#2e7d32', '#6a1b9a', '#c62828', '#ff8f00', '#00838f'];

const buildProductImageUrl = (source) => {
  if (!source || typeof source !== 'string') return '';
  if (/^https?:\/\//i.test(source)) return source;
  const cleaned = source.startsWith('/') ? source.slice(1) : source;
  const baseUrl =
    process.env.REACT_APP_DATA_PIC_PATH || process.env.REACT_APP_PEGASUS_API_URL || '';
  if (!baseUrl) return cleaned;
  return `${baseUrl}/${cleaned}`;
};

function ProductAddonsView() {
  const [addons, setAddons] = useState([]);
  const [plateforms, setPlateforms] = useState([]);
  const [formData, setFormData] = useState(createEmptyAddon());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAddonOptions, setSelectedAddonOptions] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerContext, setProductPickerContext] = useState({
    optionIndex: null,
    productIndex: null,
  });
  const [productSearch, setProductSearch] = useState('');

  const fetchAddons = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ProductAddonService.fetchProductAddons();
      setAddons(Array.isArray(data?.response) ? data.response : data || []);
    } catch (err) {
      console.error('Unable to fetch product addons', err);
      setError("Impossible de récupérer les addons produits.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlateforms = async () => {
    try {
      const data = await PlateformService.fetchPlateforms();
      setPlateforms(Array.isArray(data?.response) ? data.response : data || []);
    } catch (err) {
      console.error('Unable to fetch plateforms', err);
    }
  };

  useEffect(() => {
    fetchAddons();
    fetchPlateforms();
    const fetchProducts = async () => {
      setProductsLoading(true);
      setProductsError('');
      try {
        const response = await ProductService.fetchProducts();
        // fetchProducts now returns an array directly from NDJSON stream
        const items = Array.isArray(response) ? response : [];
        const normalized = items
          .map((item) => {
            const id = extractId(item?._id || item?.id || item?.value || item);
            const rawNameSource =
              item?.name ?? item?.description ?? (typeof item === 'string' ? item : '');
            const normalizedName =
              typeof rawNameSource === 'string' ? rawNameSource.trim() : '';
            const rawDescription =
              typeof item?.description === 'string' ? item.description.trim() : '';
            const label = normalizedName || rawDescription;
            if (!id || !label) {
              return null;
            }
            const imageSource =
              item?.url_product ||
              item?.imageUrl ||
              item?.image_url ||
              item?.main_image_url ||
              item?.main_image ||
              item?.image ||
              (Array.isArray(item?.images) ? item.images[0] : '') ||
              item?.thumbnail ||
              item?.thumb;
            return {
              id,
              name: normalizedName || rawDescription || id,
              description: rawDescription || normalizedName || '',
              isComposant: !!item?.isComposant,
              imageUrl: buildProductImageUrl(imageSource),
            };
          })
          .filter(Boolean);
        setProducts(normalized);
      } catch (productError) {
        console.error('Unable to fetch products', productError);
        setProductsError('Impossible de récupérer les produits composants.');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const productMap = useMemo(() => {
    const mapping = {};
    products.forEach((product) => {
      mapping[product.id] = product;
    });
    return mapping;
  }, [products]);

  const composantProducts = useMemo(
    () => products.filter((product) => product.isComposant),
    [products]
  );

  const composantOptions = useMemo(
    () =>
      composantProducts.map((product) => ({
        value: product.id,
        label: product.name,
      })),
    [composantProducts]
  );

  const filteredComposantProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return composantProducts;
    return composantProducts.filter((product) => {
      const haystack = `${product.name || ''} ${product.description || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [composantProducts, productSearch]);

  const plateformOptions = useMemo(
    () =>
      (plateforms || []).map((plateform) => ({
        value: extractId(plateform._id || plateform.id || plateform),
        label: plateform.name || extractId(plateform._id || plateform.id || plateform) || '—',
        type: plateform.type || '',
        raw: plateform,
      })),
    [plateforms]
  );

  const plateformById = useMemo(() => {
    const mapping = {};
    plateformOptions.forEach((option) => {
      if (option.value) {
        mapping[option.value] = option;
      }
    });
    return mapping;
  }, [plateformOptions]);

  const plateformNames = useMemo(() => {
    const mapping = {};
    plateformOptions.forEach((option) => {
      if (option.value) {
        mapping[option.value] = option.label;
      }
    });
    return mapping;
  }, [plateformOptions]);

  const openProductPicker = (optionIndex, productIndex) => {
    setProductPickerContext({ optionIndex, productIndex });
    setProductSearch('');
    setProductPickerOpen(true);
  };

  const closeProductPicker = () => {
    setProductPickerContext({ optionIndex: null, productIndex: null });
    setProductPickerOpen(false);
    setProductSearch('');
  };

  const resetForm = () => {
    setFormData(createEmptyAddon());
    setEditingId(null);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
  };

  const handleAddonFieldChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddonPlateformChange = (index, field, value) => {
    setFormData((prev) => {
      const entries = [...(prev.plateforms_keys || [])];
      entries[index] = {
        ...entries[index],
        [field]: field === 'remote_id' ? value : extractId(value),
      };
      return { ...prev, plateforms_keys: entries };
    });
  };

  const addAddonPlateform = () => {
    setFormData((prev) => ({
      ...prev,
      plateforms_keys: [...(prev.plateforms_keys || []), { plateform_id: '', remote_id: '' }],
    }));
  };

  const removeAddonPlateform = (index) => {
    setFormData((prev) => ({
      ...prev,
      plateforms_keys: prev.plateforms_keys.filter((_, i) => i !== index),
    }));
  };

  const handleOptionChange = (index, field, value) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const current = { ...options[index] };
      if (['price', 'price_delta', 'sale_price_delta', 'image_url', 'color'].includes(field)) {
        current[field] = value;
      } else if (field === 'name') {
        current[field] = value;
      }
      options[index] = current;
      return { ...prev, options };
    });
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [
        ...(prev.options || []),
        {
          name: '',
          price: 0,
          price_delta: 0,
          sale_price_delta: 0,
          image_url: '',
          color: '',
          plateforms_keys: [],
          products: [],
        },
      ],
    }));
  };

  const removeOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleOptionPlateformChange = (optionIndex, keyIndex, field, value) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const option = { ...(options[optionIndex] || { plateforms_keys: [] }) };
      const entries = [...(option.plateforms_keys || [])];
      entries[keyIndex] = {
        ...entries[keyIndex],
        [field]: field === 'remote_id' ? value : extractId(value),
      };
      option.plateforms_keys = entries;
      options[optionIndex] = option;
      return { ...prev, options };
    });
  };

  const addOptionPlateform = (optionIndex) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const option = { ...(options[optionIndex] || { plateforms_keys: [] }) };
      option.plateforms_keys = [
        ...(option.plateforms_keys || []),
        { plateform_id: '', remote_id: '' },
      ];
      options[optionIndex] = option;
      return { ...prev, options };
    });
  };

  const removeOptionPlateform = (optionIndex, keyIndex) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const option = { ...(options[optionIndex] || { plateforms_keys: [] }) };
      option.plateforms_keys = option.plateforms_keys.filter((_, i) => i !== keyIndex);
      options[optionIndex] = option;
      return { ...prev, options };
    });
  };

  const handleEdit = (addon) => {
    const normalized = {
      name: addon.name || '',
      input_type: addon.input_type || addon.type || 'select',
      required: addon.required ?? false,
      multiple: addon.multiple ?? false,
      is_inventory: addon.is_inventory ?? false,
      plateforms_keys: Array.isArray(addon.plateforms_keys)
        ? addon.plateforms_keys.map((entry) => ({
            plateform_id: extractId(entry.plateform_id),
            remote_id: entry.remote_id || '',
          }))
        : [],
      options: Array.isArray(addon.options)
        ? addon.options.map((option) => ({
            _id: extractId(option._id),
            name: option.name || '',
            price: option.price ?? 0,
            price_delta: option.price_delta ?? option.priceDelta ?? option.price ?? 0,
            sale_price_delta: option.sale_price_delta ?? option.salePriceDelta ?? 0,
            image_url: option.image_url || option.imageUrl || '',
            color: option.color || '',
            plateforms_keys: Array.isArray(option.plateforms_keys)
              ? option.plateforms_keys.map((entry) => ({
                  plateform_id: extractId(entry.plateform_id),
                  remote_id: entry.remote_id || '',
                }))
              : [],
            products: Array.isArray(option.products)
              ? option.products
                  .map((product) => {
                    const productId = extractId(
                      product.product_id || product.product || product.id
                    );
                    if (!productId) {
                      return null;
                    }
                    const quantity = Number(product.quantity);
                    const quantityValue =
                      Number.isFinite(quantity) && quantity >= 0 ? String(quantity) : '1';
                    return {
                      product_id: productId,
                      quantity: quantityValue,
                    };
                  })
                  .filter(Boolean)
              : [],
          }))
        : [],
    };

    setEditingId(extractId(addon._id || addon.id));
    setFormData(normalized);
    setIsEditorOpen(true);
  };

  const handleOptionProductChange = (optionIndex, productIndex, field, value) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const option = { ...(options[optionIndex] || { products: [] }) };
      const products = [...(option.products || [])];
      const current = { ...(products[productIndex] || { product_id: '', quantity: '1' }) };

      if (field === 'product_id') {
        current.product_id = value;
      }
      if (field === 'quantity') {
        current.quantity = value;
      }

      products[productIndex] = current;
      option.products = products;
      options[optionIndex] = option;
      return { ...prev, options };
    });
  };

  const handleProductSelect = (productId) => {
    if (productPickerContext.optionIndex === null || productPickerContext.productIndex === null) {
      return;
    }
    handleOptionProductChange(
      productPickerContext.optionIndex,
      productPickerContext.productIndex,
      'product_id',
      productId
    );
    closeProductPicker();
  };

  const addOptionProduct = (optionIndex) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const option = { ...(options[optionIndex] || { products: [] }) };
      const existing = option.products || [];
      const newProducts = [...existing, { product_id: '', quantity: '1' }];
      const newIndex = newProducts.length - 1;

      option.products = newProducts;
      options[optionIndex] = option;
      setProductPickerContext({ optionIndex, productIndex: newIndex });
      setProductPickerOpen(true);
      setProductSearch('');
      return { ...prev, options };
    });
  };

  const removeOptionProduct = (optionIndex, productIndex) => {
    setFormData((prev) => {
      const options = [...(prev.options || [])];
      const option = { ...(options[optionIndex] || { products: [] }) };
      option.products = (option.products || []).filter((_, index) => index !== productIndex);
      options[optionIndex] = option;
      return { ...prev, options };
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet addon ?')) {
      return;
    }
    try {
      await ProductAddonService.deleteProductAddon(id);
      fetchAddons();
    } catch (err) {
      console.error('Unable to delete product addon', err);
      setError("La suppression de l'addon a échoué.");
    }
  };

  const handleSubmit = async () => {
    const payload = sanitizeAddonPayload(formData, plateformById);
    if (!payload.name) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      if (editingId) {
        await ProductAddonService.updateProductAddon(editingId, payload);
      } else {
        await ProductAddonService.createProductAddon(payload);
      }
      resetForm();
      setIsEditorOpen(false);
      fetchAddons();
    } catch (err) {
      console.error('Unable to save product addon', err);
      setError("L'enregistrement de l'addon a échoué.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !formData.name || !formData.name.trim() || isSubmitting;

  return (
    <div className="container mt-4">
      <h2>Addons Produit</h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {productsError && (
        <div className="alert alert-warning" role="alert">
          {productsError}
        </div>
      )}

      <div className="text-end mb-3">
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            resetForm();
            setIsEditorOpen(true);
          }}
        >
          Ajouter un addon
        </Button>
      </div>

      {loading ? (
        <div>Chargement des addons...</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Type site</th>
                <th>Inventaire</th>
                <th>Plateformes</th>
                <th>Options</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {addons && addons.length > 0 ? (
                addons.map((addon) => {
                  const addonId = extractId(addon._id || addon.id);
                  return (
                    <tr key={addonId}>
                      <td>{addon.name}</td>
                      <td>{addonInputTypes.find((type) => type.value === (addon.input_type || addon.type))?.label || 'Liste déroulante'}</td>
                      <td>{addon.is_inventory ? 'Oui' : 'Non'}</td>
                      <td>
                        {Array.isArray(addon.plateforms_keys) && addon.plateforms_keys.length > 0 ? (
                          addon.plateforms_keys.map((entry, index) => {
                            const id = extractId(entry.plateform_id);
                            return (
                              <div key={`${addonId}-plateform-${index}`}>
                                <strong>{plateformNames[id] || id || '—'}:</strong> {entry.remote_id || '—'}
                              </div>
                            );
                          })
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td>
                        {Array.isArray(addon.options) && addon.options.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            <span>{addon.options.length} option{addon.options.length > 1 ? 's' : ''}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary align-self-start"
                              data-bs-toggle="modal"
                              data-bs-target="#addonOptionsModal"
                              onClick={() => setSelectedAddonOptions(addon)}
                            >
                              Voir les options
                            </button>
                          </div>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td className="text-nowrap">
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          className="me-2"
                          onClick={() => handleEdit(addon)}
                        >
                          Modifier
                        </Button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(addonId)}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center">
                    Aucun addon disponible.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      
      <Dialog open={isEditorOpen} onClose={handleCloseEditor} fullWidth maxWidth="lg">
        <DialogTitle>
          {editingId ? 'Modifier un addon' : 'Ajouter un addon'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={1}>
            <TextField
              label="Nom"
              name="name"
              fullWidth
              value={formData.name}
              onChange={handleAddonFieldChange}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Type d'affichage site"
                  name="input_type"
                  value={formData.input_type || 'select'}
                  onChange={handleAddonFieldChange}
                >
                  {addonInputTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      name="required"
                      color="primary"
                      checked={!!formData.required}
                      onChange={handleAddonFieldChange}
                    />
                  }
                  label="Option obligatoire"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      name="multiple"
                      color="primary"
                      checked={!!formData.multiple}
                      onChange={handleAddonFieldChange}
                    />
                  }
                  label="Multi-sélection"
                />
              </Grid>
            </Grid>
      
            <FormControlLabel
              control={
                <Switch
                  name="is_inventory"
                  color="primary"
                  checked={!!formData.is_inventory}
                  onChange={handleAddonFieldChange}
                />
              }
              label="Consommation liée à l'inventaire"
            />
      
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Correspondances plateformes
                </Typography>
                <Button variant="outlined" size="small" onClick={addAddonPlateform}>
                  Ajouter une plateforme
                </Button>
              </Box>
              {formData.plateforms_keys.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Aucune plateforme associée.
                </Typography>
              )}
              {formData.plateforms_keys.map((entry, index) => {
                const isWooCommerce = isWooCommercePlateform(plateformById[entry.plateform_id]);

                return (
                  <Grid container spacing={2} alignItems="flex-end" key={`addon-plateform-${index}`} mb={1}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        select
                        fullWidth
                        label="Plateforme"
                        value={entry.plateform_id}
                        onChange={(event) =>
                          handleAddonPlateformChange(index, 'plateform_id', event.target.value)
                        }
                      >
                        <MenuItem value="">Sélectionner</MenuItem>
                        {plateformOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label={isWooCommerce ? 'Identifiant distant (optionnel)' : 'Identifiant distant'}
                        helperText={isWooCommerce ? 'WooCommerce utilise les options internes Pegasus.' : ''}
                        value={entry.remote_id}
                        onChange={(event) =>
                          handleAddonPlateformChange(index, 'remote_id', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2} textAlign="right">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeAddonPlateform(index)}
                      >
                        Supprimer
                      </Button>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
      
            <Divider />
      
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Options
                </Typography>
                <Button variant="outlined" size="small" onClick={addOption} color="primary">
                  Ajouter une option
                </Button>
              </Box>
              {formData.options.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Aucune option.
                </Typography>
              )}
              {formData.options.map((option, optionIndex) => {
                const optionAccent = optionColors[optionIndex % optionColors.length];
                const optionBackground = `${optionAccent}1a`;
      
                return (
                  <Box
                    key={`option-${optionIndex}`}
                    mb={2}
                    p={2}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${optionAccent}`,
                      backgroundColor: optionBackground,
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                      <TextField
                        fullWidth
                        label="Nom de l'option"
                        value={option.name}
                        onChange={(event) => handleOptionChange(optionIndex, 'name', event.target.value)}
                      />
                      <TextField
                        label="Prix (MAD)"
                        type="number"
                        inputProps={{ step: '0.01', min: 0 }}
                        value={option.price}
                        onChange={(event) => handleOptionChange(optionIndex, 'price', event.target.value)}
                      />
                      <TextField
                        label="Delta prix"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        value={option.price_delta ?? ''}
                        onChange={(event) =>
                          handleOptionChange(optionIndex, 'price_delta', event.target.value)
                        }
                      />
                      <TextField
                        label="Delta prix promo"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        value={option.sale_price_delta ?? ''}
                        onChange={(event) =>
                          handleOptionChange(optionIndex, 'sale_price_delta', event.target.value)
                        }
                      />
                      <TextField
                        label="Image option"
                        placeholder="https://..."
                        value={option.image_url ?? ''}
                        onChange={(event) =>
                          handleOptionChange(optionIndex, 'image_url', event.target.value)
                        }
                      />
                      <TextField
                        label="Couleur"
                        placeholder="#d4144f"
                        value={option.color ?? ''}
                        onChange={(event) =>
                          handleOptionChange(optionIndex, 'color', event.target.value)
                        }
                      />
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => removeOption(optionIndex)}
                      >
                        Supprimer l'option
                      </Button>
                    </Stack>
      
                    <Divider sx={{ my: 2 }} />
      
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          Correspondances plateformes
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          color="info"
                          onClick={() => addOptionPlateform(optionIndex)}
                        >
                          Ajouter une plateforme
                        </Button>
                      </Box>
                      {(!option.plateforms_keys || option.plateforms_keys.length === 0) && (
                        <Typography variant="body2" color="text.secondary">
                          Aucune plateforme associée.
                        </Typography>
                      )}
                      {(option.plateforms_keys || []).map((entry, keyIndex) => {
                        const isWooCommerce = isWooCommercePlateform(plateformById[entry.plateform_id]);

                        return (
                          <Grid
                            container
                            spacing={2}
                            alignItems="flex-end"
                            key={`option-${optionIndex}-plateform-${keyIndex}`}
                            mb={1}
                          >
                            <Grid item xs={12} md={5}>
                              <TextField
                                select
                                fullWidth
                                label="Plateforme"
                                value={entry.plateform_id}
                                onChange={(event) =>
                                  handleOptionPlateformChange(
                                    optionIndex,
                                    keyIndex,
                                    'plateform_id',
                                    event.target.value
                                  )
                                }
                              >
                                <MenuItem value="">Sélectionner</MenuItem>
                                {plateformOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <TextField
                                fullWidth
                                label={isWooCommerce ? 'Identifiant distant (optionnel)' : 'Identifiant distant'}
                                helperText={isWooCommerce ? 'WooCommerce utilise les options internes Pegasus.' : ''}
                                value={entry.remote_id}
                                onChange={(event) =>
                                  handleOptionPlateformChange(
                                    optionIndex,
                                    keyIndex,
                                    'remote_id',
                                    event.target.value
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={2} textAlign="right">
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => removeOptionPlateform(optionIndex, keyIndex)}
                              >
                                Supprimer
                              </Button>
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Box>
      
                    <Divider sx={{ my: 2 }} />
      
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          Produits associés
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          onClick={() => addOptionProduct(optionIndex)}
                          disabled={composantOptions.length === 0 || productsLoading}
                        >
                          Ajouter un produit
                        </Button>
                      </Box>
                      {productsLoading && (
                        <Typography variant="body2" color="text.secondary">
                          Chargement des produits…
                        </Typography>
                      )}
                      {composantOptions.length === 0 && !productsLoading && (
                        <Typography variant="body2" color="text.secondary">
                          Aucun produit composant disponible pour être associé.
                        </Typography>
                      )}
                      {(option.products || []).map((product, productIndex) => {
                        const productInfo = productMap[extractId(product.product_id)];
                        return (
                          <Box
                            key={`option-${optionIndex}-product-${productIndex}`}
                            mb={1.5}
                            p={1.5}
                            sx={{ borderRadius: 1.5, border: '1px dashed rgba(0,0,0,0.12)' }}
                          >
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={7}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  {productInfo?.imageUrl ? (
                                    <CardMedia
                                      component="img"
                                      image={productInfo.imageUrl}
                                      alt={productInfo.name}
                                      sx={{ width: 80, height: 80, borderRadius: 1.5, objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 1.5,
                                        backgroundColor: 'rgba(0,0,0,0.04)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'text.secondary',
                                        fontSize: 12,
                                      }}
                                    >
                                      Aucune image
                                    </Box>
                                  )}
                                  <Box>
                                    <Typography fontWeight={600}>
                                      {productInfo?.name || 'Sélectionner un produit'}
                                    </Typography>
                                    {productInfo?.description && (
                                      <Typography variant="body2" color="text.secondary">
                                        {productInfo.description}
                                      </Typography>
                                    )}
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      color="secondary"
                                      sx={{ mt: 1 }}
                                      onClick={() => openProductPicker(optionIndex, productIndex)}
                                    >
                                      Choisir un produit
                                    </Button>
                                  </Box>
                                </Stack>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="Quantité"
                                  type="number"
                                  inputProps={{ min: 0, step: '0.01' }}
                                  value={product.quantity}
                                  onChange={(event) =>
                                    handleOptionProductChange(
                                      optionIndex,
                                      productIndex,
                                      'quantity',
                                      event.target.value
                                    )
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={2} textAlign="right">
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => removeOptionProduct(optionIndex, productIndex)}
                                >
                                  Supprimer
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditor}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
            {editingId ? 'Enregistrer les modifications' : "Créer l'addon"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={productPickerOpen} onClose={closeProductPicker} fullWidth maxWidth="md">
        <DialogTitle>Choisir un produit composant</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Rechercher un produit"
            placeholder="Nom ou description"
            value={productSearch}
            onChange={(event) => setProductSearch(event.target.value)}
            margin="dense"
          />
          {productsLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Chargement des produits…
            </Typography>
          )}
          {!productsLoading && filteredComposantProducts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aucun produit correspondant.
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {filteredComposantProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card
                  onClick={() => handleProductSelect(product.id)}
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.12)',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  {product.imageUrl ? (
                    <CardMedia component="img" height="140" image={product.imageUrl} alt={product.name} />
                  ) : (
                    <Box
                      sx={{
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        color: 'text.secondary',
                        fontSize: 12,
                      }}
                    >
                      Aucune image
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.description || '—'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProductPicker}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Modal
        modalId="addonOptionsModal"
        title={selectedAddonOptions ? `Options — ${selectedAddonOptions.name || '—'}` : 'Options'}
        submitLabel=""
        modalType="modal-xl"
        onClose={() => setSelectedAddonOptions(null)}
      >
        {selectedAddonOptions && Array.isArray(selectedAddonOptions.options) && selectedAddonOptions.options.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Prix</th>
                  <th>Delta prix</th>
                  <th>Delta promo</th>
                  <th>Plateformes</th>
                  <th>Produits</th>
                </tr>
              </thead>
              <tbody>
                {selectedAddonOptions.options.map((option) => {
                  const optionId = extractId(option._id || option.id || option.name);
                  return (
                    <tr key={`${extractId(selectedAddonOptions._id || selectedAddonOptions.id)}-option-${optionId}`}>
                      <td>{option.name || '—'}</td>
                      <td>{Number(option.price || 0).toFixed(2)}</td>
                      <td>{Number(option.price_delta ?? option.price ?? 0).toFixed(2)}</td>
                      <td>{Number(option.sale_price_delta || 0).toFixed(2)}</td>
                      <td>
                        {Array.isArray(option.plateforms_keys) && option.plateforms_keys.length > 0 ? (
                          option.plateforms_keys.map((entry, idx) => {
                            const id = extractId(entry.plateform_id);
                            return (
                              <div key={`${optionId}-plateform-${idx}`}>
                                <strong>{plateformNames[id] || id || '—'}:</strong> {entry.remote_id || '—'}
                              </div>
                            );
                          })
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td>
                        {Array.isArray(option.products) && option.products.length > 0 ? (
                          option.products.map((product, idx) => {
                            const productId = extractId(
                              product.product_id || product.product || product.id
                            );
                            const quantityNumber = Number(product.quantity);
                            const quantityDisplay = Number.isFinite(quantityNumber)
                              ? quantityNumber
                              : '—';
                            const productInfo = productMap[productId];
                            return (
                              <div key={`${optionId}-product-${idx}`}>
                                <strong>{productInfo?.name || productId || '—'}</strong>
                                {quantityDisplay !== '—' && (
                                  <span className="ms-2 text-muted">× {quantityDisplay}</span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div>Aucune option disponible.</div>
        )}
      </Modal>
    </div>
  );
}

export default ProductAddonsView;
